import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';

import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';

// Models and services
import { 
  CommissionPlanSlabResponse, 
  CreateCommissionPlanSlabRequest, 
  UpdateCommissionPlanSlabRequest,
  CommissionRateType,
  CommissionPlanRuleResponse,
  CommissionSalesBasis,
  OwnershipLevel
} from '../../models';
import { CommissionPlanSlabService, CommissionPlanRuleService } from '../../services';
import { ToastMessageService } from '@fitness-force/api';
import { BreadcrumbComponent } from '@fitness-force/common/breadcrumb';

@Component({
  selector: 'lib-commission-plan-slabs-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    ButtonModule,
    InputNumberModule,
    SelectModule,
    CardModule,
    BreadcrumbComponent
],
  template: `
  <div class="md:pt-3 pt-2 md:px-4 px-3 pb-8 mb-4">
    <h1 class="text-base md:text-lg text-900 flex align-items-center mb-0 line-height-1">
      {{ isEditMode ? ('COMMISSION_MANAGEMENT.SLABS.EDIT' | translate) : ('COMMISSION_MANAGEMENT.SLABS.CREATE' | translate) }}
    </h1>
    <div class="mt-1 mb-3">
      <app-breadcrumb />
    </div>
  
    <div class="card">
      <div class="flex justify-content-between align-items-center mb-4 pb-3 border-bottom-1 surface-border">
        <div>
          <p class="text-600 mt-0 mb-0">
            {{ isEditMode ? ('COMMISSION_MANAGEMENT.SLABS.EDIT_DESC' | translate) : ('COMMISSION_MANAGEMENT.SLABS.CREATE_DESC' | translate) }}
          </p>
        </div>
        <p-button [label]="'COMMISSION_MANAGEMENT.COMMON.BACK' | translate"
          icon="pi pi-arrow-left"
          [outlined]="true"
          (onClick)="goBack()"
          aria-label="back-button" />
      </div>
  
      <form [formGroup]="slabForm" class="grid">
        <!-- Rate Type and Rate Value Section -->
        <div class="col-12">
          <div class="surface-50 border-round-lg p-3 mb-3">
            <div class="flex align-items-center gap-3 mb-3">
              <div class="flex align-items-center justify-content-center bg-primary border-circle" style="width: 2rem; height: 2rem;">
                <i class="pi pi-percentage text-white text-sm"></i>
              </div>
              <div class="flex-1">
                <h3 class="text-lg font-semibold text-900 m-0">
                  {{ 'COMMISSION_MANAGEMENT.SLABS.RATE_SECTION' | translate }}
                </h3>
                <p class="text-600 text-xs m-0 mt-1">
                  {{ 'COMMISSION_MANAGEMENT.SLABS.RATE_SECTION_DESC' | translate }}
                </p>
              </div>
            </div>
  
            <div class="grid">
              <!-- Rate Type -->
              <div class="col-12 md:col-6">
                <label for="rate_type" class="font-semibold text-900 text-sm">{{ 'COMMISSION_MANAGEMENT.SLABS.RATE_TYPE_LABEL' | translate }}
                  <sup class="text-red-500">*</sup>
                </label>
                <p-select id="rate_type"
                  formControlName="rate_type"
                  [options]="rateTypeOptions"
                  optionLabel="label"
                  optionValue="value"
                  [placeholder]="'COMMISSION_MANAGEMENT.SLABS.RATE_TYPE_PLACEHOLDER' | translate"
                  class="w-full mt-2"
                  (onChange)="onRateTypeChange()"
                  aria-label="slab-rate-type-dropdown" />
                @if (slabForm.get('rate_type')?.invalid && slabForm.get('rate_type')?.touched) {
                  <small class="p-error block mt-2">
                    <i class="pi pi-exclamation-circle mr-1"></i>
                    {{ 'COMMISSION_MANAGEMENT.SLABS.RATE_TYPE_REQUIRED' | translate }}
                  </small>
                }
              </div>
  
              <!-- Rate Percentage -->
              @if (showRatePercentage) {
                <div class="col-12 md:col-6">
                  <label for="rate_percentage" class="font-semibold text-900 text-sm">{{ 'COMMISSION_MANAGEMENT.SLABS.RATE_VALUE_LABEL' | translate }}
                    <sup class="text-red-500">*</sup>
                  </label>
                  <p-inputNumber id="rate_percentage"
                    formControlName="rate_percentage"
                    mode="decimal"
                    [min]="0"
                    [max]="100"
                    suffix="%"
                    [minFractionDigits]="0"
                    [maxFractionDigits]="2"
                    class="w-full mt-2"
                    [placeholder]="'COMMISSION_MANAGEMENT.SLABS.RATE_VALUE_PLACEHOLDER' | translate"
                    aria-label="rate-percentage-input" />
                  @if (slabForm.get('rate_percentage')?.invalid && slabForm.get('rate_percentage')?.touched) {
                    <small class="p-error block mt-2">
                      <i class="pi pi-exclamation-circle mr-1"></i>
                      {{ 'COMMISSION_MANAGEMENT.SLABS.RATE_VALUE_REQUIRED' | translate }}
                    </small>
                  }
                </div>
              }
  
              <!-- Rate Flat Amount -->
              @if (showRateFlatAmount) {
                <div class="col-12 md:col-6">
                  <label for="rate_flat_amount" class="font-semibold text-900 text-sm">{{ 'COMMISSION_MANAGEMENT.SLABS.RATE_VALUE_LABEL' | translate }}
                    <sup class="text-red-500">*</sup>
                  </label>
                  <p-inputNumber id="rate_flat_amount"
                    formControlName="rate_flat_amount"
                    mode="decimal"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="2"
                    [min]="0"
                    class="w-full mt-2"
                    [placeholder]="'COMMISSION_MANAGEMENT.SLABS.RATE_VALUE_PLACEHOLDER' | translate"
                    aria-label="rate-flat-amount-input" />
                  @if (slabForm.get('rate_flat_amount')?.invalid && slabForm.get('rate_flat_amount')?.touched) {
                    <small class="p-error block mt-2">
                      <i class="pi pi-exclamation-circle mr-1"></i>
                      {{ 'COMMISSION_MANAGEMENT.SLABS.RATE_VALUE_REQUIRED' | translate }}
                    </small>
                  }
                </div>
              }
            </div>
          </div>
        </div>
  
        <!-- Quantity-based Thresholds (for on_sale and session_count) -->
        @if (showQuantityThresholds) {
          <div class="col-12">
            <div class="surface-50 border-round-lg p-3 mb-3">
              <div class="flex align-items-center gap-3 mb-3">
                <div class="flex align-items-center justify-content-center bg-cyan-500 border-circle" style="width: 2rem; height: 2rem;">
                  <i class="pi pi-hashtag text-white text-sm"></i>
                </div>
                <div class="flex-1">
                  <h3 class="text-lg font-semibold text-900 m-0">
                    {{ 'COMMISSION_MANAGEMENT.SLABS.QUANTITY_THRESHOLDS' | translate }}
                  </h3>
                  <p class="text-600 text-xs m-0 mt-1">
                    {{ 'COMMISSION_MANAGEMENT.SLABS.QUANTITY_THRESHOLDS_DESC' | translate }}
                  </p>
                </div>
              </div>
              <div class="grid">
                <!-- Quantity From -->
                <div class="col-12 md:col-6">
                  <label for="quantity_from" class="font-semibold text-900 text-sm">{{ 'COMMISSION_MANAGEMENT.SLABS.QUANTITY_FROM_LABEL' | translate }}</label>
                  <p-inputNumber id="quantity_from"
                    formControlName="quantity_from"
                    mode="decimal"
                    [minFractionDigits]="0"
                    [maxFractionDigits]="0"
                    [min]="0"
                    class="w-full mt-2"
                    [placeholder]="'COMMISSION_MANAGEMENT.SLABS.QUANTITY_FROM_PLACEHOLDER' | translate"
                    aria-label="quantity-from-input" />
                </div>
                <!-- Quantity To -->
                <div class="col-12 md:col-6">
                  <label for="quantity_to" class="font-semibold text-900 text-sm">{{ 'COMMISSION_MANAGEMENT.SLABS.QUANTITY_TO_LABEL' | translate }}</label>
                  <p-inputNumber id="quantity_to"
                    formControlName="quantity_to"
                    mode="decimal"
                    [minFractionDigits]="0"
                    [maxFractionDigits]="0"
                    [min]="0"
                    class="w-full mt-2"
                    [placeholder]="'COMMISSION_MANAGEMENT.SLABS.QUANTITY_TO_PLACEHOLDER' | translate"
                    aria-label="quantity-to-input" />
                </div>
              </div>
            </div>
          </div>
        }
  
        <!-- Revenue-based Thresholds (for on_revenue and on_payment) -->
        @if (showRevenueThresholds) {
          <div class="col-12">
            <div class="surface-50 border-round-lg p-3 mb-3">
              <div class="flex align-items-center gap-3 mb-3">
                <div class="flex align-items-center justify-content-center bg-green-500 border-circle" style="width: 2rem; height: 2rem;">
                  <i class="pi pi-dollar text-white text-sm"></i>
                </div>
                <div class="flex-1">
                  <h3 class="text-lg font-semibold text-900 m-0">
                    {{ 'COMMISSION_MANAGEMENT.SLABS.REVENUE_THRESHOLDS' | translate }}
                  </h3>
                  <p class="text-600 text-xs m-0 mt-1">
                    {{ 'COMMISSION_MANAGEMENT.SLABS.REVENUE_THRESHOLDS_DESC' | translate }}
                  </p>
                </div>
              </div>
              <div class="grid">
                <!-- Revenue From -->
                <div class="col-12 md:col-6">
                  <label for="revenue_from" class="font-semibold text-900 text-sm">{{ 'COMMISSION_MANAGEMENT.SLABS.REVENUE_FROM_LABEL' | translate }}</label>
                  <p-inputNumber id="revenue_from"
                    formControlName="revenue_from"
                    mode="decimal"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="2"
                    [min]="0"
                    class="w-full mt-2"
                    [placeholder]="'COMMISSION_MANAGEMENT.SLABS.REVENUE_FROM_PLACEHOLDER' | translate"
                    aria-label="revenue-from-input" />
                </div>
                <!-- Revenue To -->
                <div class="col-12 md:col-6">
                  <label for="revenue_to" class="font-semibold text-900 text-sm">{{ 'COMMISSION_MANAGEMENT.SLABS.REVENUE_TO_LABEL' | translate }}</label>
                  <p-inputNumber id="revenue_to"
                    formControlName="revenue_to"
                    mode="decimal"
                    [minFractionDigits]="2"
                    [maxFractionDigits]="2"
                    [min]="0"
                    class="w-full mt-2"
                    [placeholder]="'COMMISSION_MANAGEMENT.SLABS.REVENUE_TO_PLACEHOLDER' | translate"
                    aria-label="revenue-to-input" />
                </div>
              </div>
            </div>
          </div>
        }
  
        <!-- Form Actions -->
        <div class="col-12">
          <div class="flex justify-content-end gap-2 mt-3 pt-3 border-top-1 surface-border">
            <p-button [label]="'COMMISSION_MANAGEMENT.COMMON.CANCEL' | translate"
              icon="pi pi-times"
              [outlined]="true"
              severity="secondary"
              (onClick)="goBack()"
              aria-label="cancel-button" />
            <p-button [label]="'COMMISSION_MANAGEMENT.COMMON.SAVE' | translate"
              icon="pi pi-check"
              [loading]="saving"
              [disabled]="slabForm.invalid"
              (onClick)="saveSlab()"
              aria-label="save-slab-button" />
          </div>
        </div>
      </form>
    </div>
  </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommissionPlanSlabsFormComponent implements OnInit {
  private fb = inject(FormBuilder);
  private commissionPlanSlabService = inject(CommissionPlanSlabService);
  private commissionPlanRuleService = inject(CommissionPlanRuleService);
  private toastService = inject(ToastMessageService);
  private translate = inject(TranslateService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  slabForm: FormGroup;
  saving = false;
  planId!: number;
  ruleId!: number;
  ownership!: string;
  slabId?: number;
  slab?: CommissionPlanSlabResponse;
  rule?: CommissionPlanRuleResponse;
  salesBasis?: CommissionSalesBasis;

  rateTypeOptions = [
    { label: 'Percentage', value: CommissionRateType.Percentage },
    { label: 'Flat Fee', value: CommissionRateType.FlatFee }
  ];

  get isEditMode(): boolean {
    return !!this.slabId;
  }

  get showRatePercentage(): boolean {
    return this.slabForm.get('rate_type')?.value === CommissionRateType.Percentage;
  }

  get showRateFlatAmount(): boolean {
    return this.slabForm.get('rate_type')?.value === CommissionRateType.FlatFee;
  }

  get showQuantityThresholds(): boolean {
    return this.salesBasis === CommissionSalesBasis.OnSale || 
           this.salesBasis === CommissionSalesBasis.SessionCount;
  }

  get showRevenueThresholds(): boolean {
    return this.salesBasis === CommissionSalesBasis.OnRevenue || 
           this.salesBasis === CommissionSalesBasis.OnPayment;
  }

  constructor() {
    this.slabForm = this.fb.group({
      rate_type: [null, Validators.required],
      quantity_from: [null],
      quantity_to: [null],
      revenue_from: [null],
      revenue_to: [null],
      rate_percentage: [null],
      rate_flat_amount: [null]
    });
  }

  ngOnInit() {
    this.route.params.subscribe(params => {
      this.planId = +params['planId'];
      this.ruleId = +params['ruleId'];
      const ownershipParam = params['ownership'] as OwnershipLevel;
      this.ownership = ownershipParam && Object.values(OwnershipLevel).includes(ownershipParam)
        ? ownershipParam
        : OwnershipLevel.Company;

      // Load the rule first to get sales basis
      this.loadRule();

      if (params['slabId']) {
        this.slabId = +params['slabId'];
        this.loadSlab();
      }
    });
  }

  loadRule() {
    this.commissionPlanRuleService.getCommissionPlanRule(this.planId, this.ruleId).subscribe({
      next: (rule: CommissionPlanRuleResponse) => {
        this.rule = rule;
        this.salesBasis = rule.sales_basis;
        this.changeDetectorRef.detectChanges();
      },
      error: (error) => {
        this.toastService.showError(error);
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  onRateTypeChange() {
    const rateType = this.slabForm.get('rate_type')?.value;
    
    // Clear and update validators based on rate type
    if (rateType === CommissionRateType.Percentage) {
      this.slabForm.get('rate_percentage')?.setValidators([Validators.required]);
      this.slabForm.get('rate_flat_amount')?.clearValidators();
      this.slabForm.patchValue({ rate_flat_amount: null });
    } else if (rateType === CommissionRateType.FlatFee) {
      this.slabForm.get('rate_flat_amount')?.setValidators([Validators.required]);
      this.slabForm.get('rate_percentage')?.clearValidators();
      this.slabForm.patchValue({ rate_percentage: null });
    }
    
    this.slabForm.get('rate_percentage')?.updateValueAndValidity();
    this.slabForm.get('rate_flat_amount')?.updateValueAndValidity();
    this.changeDetectorRef.detectChanges();
  }

  loadSlab() {
    if (this.slabId) {
      this.commissionPlanSlabService.getCommissionPlanSlab(this.planId, this.ruleId, this.slabId).subscribe({
        next: (slab: CommissionPlanSlabResponse) => {
          this.slab = slab;
          this.populateForm();
          this.changeDetectorRef.detectChanges();
        },
        error: (error) => {
          this.toastService.showError(error);
          this.changeDetectorRef.detectChanges();
        }
      });
    }
  }

  populateForm() {
    if (this.slab) {
      this.slabForm.patchValue({
        rate_type: this.slab.rate_type,
        quantity_from: this.slab.quantity_from,
        quantity_to: this.slab.quantity_to,
        revenue_from: this.slab.revenue_from,
        revenue_to: this.slab.revenue_to,
        rate_percentage: this.slab.rate_percentage,
        rate_flat_amount: this.slab.rate_flat_amount
      });
      // Trigger validation updates
      this.onRateTypeChange();
    }
  }

  saveSlab() {
    if (this.slabForm.valid) {
      this.saving = true;
      const formData = this.slabForm.value;

      const request: CreateCommissionPlanSlabRequest | UpdateCommissionPlanSlabRequest = {
        rate_type: formData.rate_type,
        quantity_from: formData.quantity_from,
        quantity_to: formData.quantity_to,
        revenue_from: formData.revenue_from,
        revenue_to: formData.revenue_to,
        rate_percentage: formData.rate_percentage,
        rate_flat_amount: formData.rate_flat_amount
      };

      if (this.isEditMode && this.slabId) {
        this.commissionPlanSlabService.updateCommissionPlanSlab(this.planId, this.ruleId, this.slabId, request as UpdateCommissionPlanSlabRequest).subscribe({
          next: () => {
            this.saving = false;
            this.toastService.showSuccess(this.translate.instant('COMMISSION_MANAGEMENT.SLABS.UPDATE_SUCCESS'));
            this.changeDetectorRef.detectChanges();
            this.goBack();
          },
          error: (error: unknown) => {
            this.saving = false;
            this.toastService.showError(error);
            this.changeDetectorRef.detectChanges();
          }
        });
      } else {
        this.commissionPlanSlabService.createCommissionPlanSlab(this.planId, this.ruleId, request as CreateCommissionPlanSlabRequest).subscribe({
          next: () => {
            this.saving = false;
            this.toastService.showSuccess(this.translate.instant('COMMISSION_MANAGEMENT.SLABS.CREATE_SUCCESS'));
            this.changeDetectorRef.detectChanges();
            this.goBack();
          },
          error: (error: unknown) => {
            this.saving = false;
            this.toastService.showError(error);
            this.changeDetectorRef.detectChanges();
          }
        });
      }
    }
  }

  goBack() {
    this.router.navigate([
      '/admin/commission-management/plans',
      this.ownership,
      this.planId,
      'rules',
      this.ruleId,
      'slabs'
    ]);
  }
}

